import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import {
  buildSelfServiceApiUrl,
  extractStringArray,
  getSelfServicePlatformApiUrl,
  getSelfServicePlatformGetDataApiUrl,
  getSelfServiceSchemaListApiUrl,
  getSelfServiceUploadApiUrl,
  getSelfServiceWorkflowDeleteFilesApiUrl,
  getSelfServiceWorkflowListApiUrl,
  getSelfServiceWorkflowRunApiUrl,
  getSelfServiceWorkflowUploadApiUrl,
  unwrapArray,
  unwrapObject,
} from './self-service-api.utils';

export interface DatasetFormulaPayload {
  computedColumns: Array<{ name: string; expression: string }>;
  rowFilter: string;
  customAggregations: Array<{ key: string; label: string; expression: string }>;
}

export interface EntityFilterPayload {
  operator: string;
  attributeName: string;
  attributeValue: string[] | null;
  logicalOperator: string;
}

export interface ExecuteEntityQueryPayload {
  entityName: string;
  attributesList?: string[];
  filterList?: EntityFilterPayload[];
}

export interface EntityRowUpdate {
  keyValue: unknown;
  changes: Record<string, unknown>;
}

export interface UpdateEntityRowsPayload {
  entityName: string;
  keyColumn: string;
  rows: EntityRowUpdate[];
}

export interface UpdateEntityRowsResponse {
  status: string;
  entityName?: string;
  rowsUpdated?: number;
  rowsDeleted?: number;
  message?: string;
}

export interface InsertEntityRowPayload {
  entityName: string;
  values: Record<string, unknown>;
}

export interface InsertEntityRowResponse {
  status: string;
  entityName?: string;
  row?: { keyColumn: string; keyValue: unknown };
  message?: string;
}

export interface DeleteEntityRowPayload {
  entityName: string;
  keyColumn: string;
  keyValue: unknown;
}

export interface LookupEntityMetadata {
  entity: string;
  attributes: string[];
}

interface DatasetUploadResponse {
  datasetId?: string;
  fileName?: string;
  columns?: Array<{ name: string; type?: string }>;
  rowCount?: number;
  previewRows?: Array<Record<string, unknown>>;
}

@Injectable({
  providedIn: 'root',
})
export class SelfServiceDatasetService {
  private static readonly DATASET_FORMULAS_STORAGE_KEY = 'dvm.selfService.datasetFormulas';
  private latestWorkflowDeamResponse: unknown | null = null;
  private latestWorkflowStoreResponse: unknown | null = null;
  private latestWorkflowId = '';

  constructor(private readonly http: HttpClient) {}

  getLatestWorkflowDeamResponse(): unknown | null {
    return this.latestWorkflowDeamResponse;
  }

  getLatestWorkflowStoreResponse(): unknown | null {
    return this.latestWorkflowStoreResponse;
  }

  getLatestWorkflowId(): string {
    return this.latestWorkflowId;
  }

  clearLatestWorkflow(): void {
    this.latestWorkflowDeamResponse = null;
    this.latestWorkflowStoreResponse = null;
    this.latestWorkflowId = '';
  }

  uploadDataset(files: File[]): Observable<DatasetUploadResponse | null> {
    const formData = new FormData();
    files.forEach((file) => {
      formData.append('file', file, file.name);
    });

    return this.http
      .post<DatasetUploadResponse>(
        getSelfServiceUploadApiUrl(),
        formData,
      )
      .pipe(
        map((response) => unwrapObject<DatasetUploadResponse>(response, ['data']) || response),
        catchError(() => of(null)),
      );
  }

  /** @param schemaName The schema (existing or newly named) the uploaded
   *  source file(s) should be filed under — from the Data Builder's
   *  Existing/New schema selector. Omitted/empty when the backend doesn't
   *  need one. */
  replaceWorkflowSourceFiles(files: File[], schemaName?: string): Observable<unknown> {
    this.latestWorkflowDeamResponse = null;
    this.latestWorkflowStoreResponse = null;
    this.latestWorkflowId = '';
    const formData = new FormData();
    const uploadEntityNames = files.map((file) => this.workflowEntityName(file.name));
    files.forEach((file) => {
      formData.append('file', file, file.name);
    });
    uploadEntityNames.forEach((entityName) => {
      formData.append('entityName', entityName);
    });
    const trimmedSchemaName = String(schemaName ?? '').trim();
    if (trimmedSchemaName) {
      formData.append('schemaName', trimmedSchemaName);
    }

    return this.http
      .delete(getSelfServiceWorkflowDeleteFilesApiUrl())
      .pipe(
        switchMap(() => this.http.post<unknown>(getSelfServiceWorkflowUploadApiUrl(), formData)),
        switchMap((uploadResponse) => {
          const responseEntityNames = this.extractWorkflowUploadEntityNames(uploadResponse);
          const entityNames = responseEntityNames.length ? responseEntityNames : uploadEntityNames;
          this.latestWorkflowId = entityNames[0] || uploadEntityNames[0] || '';
          const entityName = this.latestWorkflowId;
          return this.http
            .post(getSelfServicePlatformGetDataApiUrl(), {
              attributesList: ['payload'],
              filterList: [
                {
                  operator: '=',
                  attributeName: 'entity_name',
                  attributeValue: entityName,
                  logicalOperator: '',
                },
              ],
              entityName: 'entity_store',
            })
            .pipe(
              tap((entityStoreResponse) => {
                this.latestWorkflowDeamResponse = this.toWorkflowDeamResponse(
                  entityStoreResponse,
                  entityName,
                );
              }),
              switchMap(() => this.fetchWorkflowStore(entityName)),
            );
        }),
      );
  }

  /** `responseType: 'text'` is required — this endpoint acknowledges in plain
   *  text, and the default 'json' fails to parse it and errors on a 200. */
  runWorkflow(workflowId: string): Observable<unknown> {
    return this.http.post(getSelfServiceWorkflowRunApiUrl(workflowId), '', { responseType: 'text' });
  }

  refreshLatestWorkflowStore(): Observable<unknown> {
    return this.fetchWorkflowStore(this.latestWorkflowId);
  }

  updateWorkflowStore(payload: unknown): Observable<unknown> {
    return this.http.post(getSelfServicePlatformApiUrl(), payload).pipe(
      tap(() => {
        const payloadRecord = payload && typeof payload === 'object' && !Array.isArray(payload)
          ? payload as Record<string, unknown>
          : null;
        const requestData = payloadRecord?.['requestData'];
        const requestDataRecord = requestData && typeof requestData === 'object' && !Array.isArray(requestData)
          ? requestData as Record<string, unknown>
          : null;
        const workflows = requestDataRecord?.['workflows'];

        // Keep the workflow JSON that was successfully submitted. The update
        // endpoint commonly returns only an acknowledgement, which cannot be
        // used as the base metadata for a subsequent Transformation operation.
        if (workflows && typeof workflows === 'object' && !Array.isArray(workflows)) {
          const workflowId = String(
            requestDataRecord?.['workflow_id']
              || payloadRecord?.['workflowId']
              || this.latestWorkflowId,
          );
          this.latestWorkflowStoreResponse = [{
            workflows,
            workflow_id: workflowId,
            uuid: requestDataRecord?.['uuid'],
          }];
        }
      }),
    );
  }

  private fetchWorkflowStore(entityName: string): Observable<unknown> {
    const workflowId = String(entityName || '').trim();
    return this.http
      .post(getSelfServicePlatformGetDataApiUrl(), {
        attributesList: ['workflows', 'workflow_id', 'uuid'],
        filterList: [
          {
            operator: '=',
            attributeName: 'workflow_id',
            attributeValue: workflowId,
            logicalOperator: '',
          },
        ],
        entityName: 'workflow_store',
      })
      .pipe(tap((workflowStoreResponse) => {
        this.latestWorkflowStoreResponse = workflowStoreResponse;
      }));
  }

  private extractWorkflowUploadEntityNames(response: unknown): string[] {
    return Array.from(new Set(this.findWorkflowUploadEntityNames(response)));
  }

  private toWorkflowDeamResponse(response: unknown, requestedEntityName: string): unknown {
    const deam = this.findEntityStoreDeam(response, requestedEntityName);
    if (!deam) return response;

    const entityName = String(deam['entity'] || requestedEntityName).trim();
    return {
      deams: [{
        deam,
        entityName,
      }],
    };
  }

  private findEntityStoreDeam(
    value: unknown,
    requestedEntityName: string,
  ): Record<string, unknown> | null {
    if (Array.isArray(value)) {
      for (const item of value) {
        const match = this.findEntityStoreDeam(item, requestedEntityName);
        if (match) return match;
      }
      return null;
    }
    if (!value || typeof value !== 'object') return null;

    const record = value as Record<string, unknown>;
    const entityName = String(record['entity'] || '').trim();
    const matchesRequestedEntity = !requestedEntityName || entityName === requestedEntityName;
    if (Array.isArray(record['attributesList']) && matchesRequestedEntity) {
      return record;
    }

    for (const nestedValue of Object.values(record)) {
      const match = this.findEntityStoreDeam(nestedValue, requestedEntityName);
      if (match) return match;
    }
    return null;
  }

  private findWorkflowUploadEntityNames(value: unknown): string[] {
    if (typeof value === 'string') {
      const trimmed = value.trim();
      return trimmed ? [trimmed] : [];
    }
    if (!value || typeof value !== 'object') return [];
    if (Array.isArray(value)) {
      return value.flatMap((item) => this.findWorkflowUploadEntityNames(item));
    }

    const record = value as Record<string, unknown>;
    const entityNames: string[] = [];
    for (const key of ['entityName', 'entity', 'entity_name', 'tableName', 'table', 'name']) {
      if (typeof record[key] === 'string' && String(record[key]).trim()) {
        entityNames.push(String(record[key]).trim());
      } else if (Array.isArray(record[key])) {
        entityNames.push(...this.findWorkflowUploadEntityNames(record[key]));
      }
    }
    for (const key of ['data', 'result', 'response', 'file', 'files']) {
      entityNames.push(...this.findWorkflowUploadEntityNames(record[key]));
    }
    return entityNames;
  }

  private workflowEntityName(fileName: string): string {
    return String(fileName || 'uploaded_source')
      .replace(/\.[^.]+$/, '')
      .trim()
      .replace(/[^A-Za-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .toLowerCase()
      || 'uploaded_source';
  }

  getEntities(entityGroup = 'NontimeSeriesEntities'): Observable<string[]> {
    return this.http
      .post<unknown>(
        buildSelfServiceApiUrl(`/getAllEntities/${encodeURIComponent(entityGroup)}`),
        { entityGroup },
      )
      .pipe(map((response) => extractStringArray(response)));
  }

  /** List of available schemas, for the Data Builder's "Schema" dropdown.
   *  Response may be a bare string array or an array of objects — unwraps
   *  common wrapper keys, then reads `schemaName`/`schema`/`name` off each
   *  object entry (falls back to `String(item)` for anything else). */
  getSchemas(): Observable<string[]> {
    return this.http.get<unknown>(getSelfServiceSchemaListApiUrl()).pipe(
      map((response) => unwrapArray<unknown>(response, ['items', 'data', 'results', 'schemas'])
        .map((item) => (typeof item === 'string'
          ? item
          : String((item as Record<string, unknown>)?.['schemaName'] ?? (item as Record<string, unknown>)?.['schema'] ?? (item as Record<string, unknown>)?.['name'] ?? '')))
        .map((value) => value.trim())
        .filter((value) => value.length > 0)),
    );
  }

  /** Creates a new schema (backend calls it a "project") for the Data
   *  Builder's "New schema" mode — POST to the SAME URL `getSchemas()` GETs
   *  from (`apiEndpoints.schemaListUrl`, e.g. `/projects/schema`), with body
   *  `{ projectName }` per the backend's contract. */
  createSchema(projectName: string): Observable<unknown> {
    return this.http.post<unknown>(getSelfServiceSchemaListApiUrl(), { projectName });
  }

  /** List of available workflows, for the Data Builder's "Workflow" dropdown.
   *  Same flexible unwrap/field-read as getSchemas — reads
   *  `workflowName`/`workflowId`/`name` off each object entry. */
  getWorkflows(): Observable<string[]> {
    return this.http.get<unknown>(getSelfServiceWorkflowListApiUrl()).pipe(
      map((response) => unwrapArray<unknown>(response, ['items', 'data', 'results', 'workflows'])
        .map((item) => (typeof item === 'string'
          ? item
          : String((item as Record<string, unknown>)?.['workflowName'] ?? (item as Record<string, unknown>)?.['workflowId'] ?? (item as Record<string, unknown>)?.['name'] ?? '')))
        .map((value) => value.trim())
        .filter((value) => value.length > 0)),
    );
  }

  getLookupEntityMetadata(): Observable<LookupEntityMetadata[]> {
    return this.http
      .post<unknown>(
        getSelfServicePlatformGetDataApiUrl(),
        {
          attributesList: ['payload'],
          filterList: [
            {
              operator: '=',
              attributeName: 'is_enabled',
              attributeValue: true,
              logicalOperator: '',
            },
          ],
          entityName: 'entity_store',
        },
      )
      .pipe(map((response) => this.extractLookupEntityMetadata(response)));
  }

  private extractLookupEntityMetadata(response: unknown): LookupEntityMetadata[] {
    const metadataByEntity = new Map<string, Set<string>>();

    const visit = (value: unknown): void => {
      if (Array.isArray(value)) {
        value.forEach(visit);
        return;
      }
      if (!value || typeof value !== 'object') return;

      const record = value as Record<string, unknown>;
      const entity = typeof record['entity'] === 'string' ? record['entity'].trim() : '';
      const attributesList = Array.isArray(record['attributesList']) ? record['attributesList'] : [];
      if (entity && attributesList.length) {
        const attributes = metadataByEntity.get(entity) ?? new Set<string>();
        attributesList.forEach((attribute) => {
          if (!attribute || typeof attribute !== 'object' || Array.isArray(attribute)) return;
          const name = String((attribute as Record<string, unknown>)['name'] || '').trim();
          if (name) attributes.add(name);
        });
        metadataByEntity.set(entity, attributes);
      }

      for (const key of ['payload', 'data', 'result', 'response', 'items', 'records']) {
        if (key in record) visit(record[key]);
      }
    };

    visit(response);
    return Array.from(metadataByEntity.entries())
      .map(([entity, attributes]) => ({
        entity,
        attributes: Array.from(attributes).sort((left, right) => left.localeCompare(right)),
      }))
      .sort((left, right) => left.entity.localeCompare(right.entity));
  }

  /**
   * Column/attribute names for an entity, straight from the metadata endpoint
   * `GET {base}/getEntityAttributes/{entityGroup}/{entityName}` — preferred over
   * inferring columns from `getEntityRows` (which fetched full rows just to read
   * their keys). Base URL is config-driven via `apiEndpoints.baseUrl`. Normalizes
   * the response to a plain string[] of column names (accepts a raw string array,
   * or objects keyed by name/attributeName/columnName/column/field/label, under
   * a top-level array or a data/attributes/columns/result wrapper).
   */
  getEntityAttributes(
    entityName: string,
    entityGroup = 'NontimeSeriesEntities',
  ): Observable<string[]> {
    return this.http
      .get<unknown>(
        buildSelfServiceApiUrl(
          `/getEntityAttributes/${encodeURIComponent(entityGroup)}/${encodeURIComponent(entityName)}`,
        ),
      )
      .pipe(map((response) => this.extractAttributeNames(response)));
  }

  private extractAttributeNames(response: unknown): string[] {
    const list = unwrapArray<unknown>(response, [
      'data',
      'attributes',
      'columns',
      'result',
      'items',
      'records',
    ]);
    const names = list.map((item) => {
      if (typeof item === 'string') return item;
      if (item && typeof item === 'object') {
        const record = item as Record<string, unknown>;
        return String(
          record['name'] ??
            record['attributeName'] ??
            record['columnName'] ??
            record['column'] ??
            record['field'] ??
            record['label'] ??
            '',
        );
      }
      return '';
    });
    return names.map((name) => name.trim()).filter((name) => name.length > 0);
  }

  getEntityRows(
    entityName: string,
  ): Observable<Array<Record<string, unknown>>> {
    return this.http
      .post<unknown>(
        buildSelfServiceApiUrl(`/${encodeURIComponent(entityName)}/dynamic_filter`),
        { uuid: null },
      )
      .pipe(
        map((response) => unwrapArray<Record<string, unknown>>(response, ['data', 'items', 'results'])),
      );
  }

  /** A dropdown's OWN option values, straight from `apiEndpoints.filterApi`
   *  (`/getData`) scoped to just its field — NOT `getEntityRows` (which pulls
   *  every row of the whole dataset over `.../dynamic_filter` just so the
   *  builder can dedupe one column client-side). `attributesList` narrows the
   *  query to `field` alone, so the backend does the filtering/distincting
   *  instead of the browser downloading and deduping the full table. */
  getDistinctFieldValues(
    url: string,
    entityName: string,
    field: string,
  ): Observable<Array<Record<string, unknown>>> {
    return this.http
      .post<unknown>(url, {
        attributesList: [field],
        filterList: [
          { operator: 'IS NOT', attributeName: 'uuid', attributeValue: null, logicalOperator: '' },
        ],
        entityName,
      })
      .pipe(
        map((response) => unwrapArray<Record<string, unknown>>(response, ['data', 'items', 'results'])),
      );
  }

  executeEntityQuery(payload: ExecuteEntityQueryPayload): Observable<Array<Record<string, unknown>>> {
    return this.http
      .post<unknown>(
        buildSelfServiceApiUrl('/persistComputedColumn'),
        payload,
      )
      .pipe(
        map((response) => this.unwrapEntityRows(response)),
      );
  }

  updateEntityRows(payload: UpdateEntityRowsPayload): Observable<UpdateEntityRowsResponse> {
    return this.http.post<UpdateEntityRowsResponse>(
      buildSelfServiceApiUrl('/updateEntityData'),
      payload,
    );
  }

  persistComputedColumnValues(payload: UpdateEntityRowsPayload): Observable<UpdateEntityRowsResponse> {
    // Wrapper specifically for computed column persistence; backend endpoint may register metadata and persist values.
    return this.http.post<UpdateEntityRowsResponse>(
      buildSelfServiceApiUrl('/persistComputedColumnValues'),
      payload,
    );
  }

  insertEntityRow(payload: InsertEntityRowPayload): Observable<InsertEntityRowResponse> {
    return this.http.post<InsertEntityRowResponse>(
      buildSelfServiceApiUrl('/insertEntityRow'),
      payload,
    );
  }

  deleteEntityRow(payload: DeleteEntityRowPayload): Observable<UpdateEntityRowsResponse> {
    return this.http.post<UpdateEntityRowsResponse>(
      buildSelfServiceApiUrl('/deleteEntityRow'),
      payload,
    );
  }

  private unwrapEntityRows(response: unknown): Array<Record<string, unknown>> {
    return this.findEntityRows(response, new Set()) || [];
  }

  private findEntityRows(value: unknown, visited: Set<unknown>): Array<Record<string, unknown>> | null {
    if (Array.isArray(value)) {
      const rows = value.filter((item): item is Record<string, unknown> => (
        !!item && typeof item === 'object' && !Array.isArray(item)
      ));
      if (this.isLikelyEntityDataRows(rows)) {
        return rows;
      }

      for (const item of rows) {
        const nestedRows = this.findEntityRows(item, visited);
        if (nestedRows?.length) {
          return nestedRows;
        }
      }

      return null;
    }

    if (!value || typeof value !== 'object' || visited.has(value)) {
      return null;
    }

    visited.add(value);
    const record = value as Record<string, unknown>;
    const preferredKeys = [
      'data',
      'rows',
      'items',
      'results',
      'records',
      'responseData',
      'result',
      'payload',
      'content',
      'computedData',
      'tableData',
    ];

    for (const key of preferredKeys) {
      if (key in record) {
        const rows = this.findEntityRows(record[key], visited);
        if (rows?.length) {
          return rows;
        }
      }
    }

    for (const nestedValue of Object.values(record)) {
      const rows = this.findEntityRows(nestedValue, visited);
      if (rows?.length) {
        return rows;
      }
    }

    return null;
  }

  private isLikelyEntityDataRows(rows: Array<Record<string, unknown>>): boolean {
    if (!rows.length) {
      return false;
    }

    const looksLikeFilterPayload = rows.every((row) => (
      'attributeName' in row &&
      'operator' in row &&
      'attributeValue' in row
    ));
    const looksLikeFormulaPayload = rows.every((row) => (
      'name' in row &&
      'expression' in row &&
      Object.keys(row).length <= 3
    ));

    return !looksLikeFilterPayload && !looksLikeFormulaPayload;
  }

  getDatasetPreview(datasetId: string): Observable<unknown> {
    return this.http.get(
      buildSelfServiceApiUrl(`/api/dvm/self-service/datasets/${encodeURIComponent(datasetId)}/preview`),
    );
  }

  getDatasetRows(datasetId: string, offset = 0, limit = 10000): Observable<unknown> {
    const params = new HttpParams()
      .set('offset', String(offset))
      .set('limit', String(limit));

    return this.http.get(
      buildSelfServiceApiUrl(`/api/dvm/self-service/datasets/${encodeURIComponent(datasetId)}/rows`),
      { params },
    );
  }

  deleteDataset(datasetId: string): Observable<void> {
    return this.http
      .delete<void>(
        buildSelfServiceApiUrl(`/api/dvm/self-service/datasets/${encodeURIComponent(datasetId)}`),
      )
      .pipe(catchError(() => of(void 0)));
  }

  getFormulas(datasetId: string | null, fileName: string): Observable<DatasetFormulaPayload> {
    if (!datasetId) {
      return of(this.readLocalFormulas(fileName));
    }

    return this.http
      .get<DatasetFormulaPayload>(
        buildSelfServiceApiUrl(`/api/dvm/self-service/datasets/${encodeURIComponent(datasetId)}/formulas`),
      )
      .pipe(
        map((response) => this.normalizeFormulaPayload(response)),
        catchError(() => of(this.readLocalFormulas(fileName))),
      );
  }

  saveFormulas(
    datasetId: string | null,
    fileName: string,
    payload: DatasetFormulaPayload,
  ): Observable<DatasetFormulaPayload> {
    if (!datasetId) {
      this.writeLocalFormulas(fileName, payload);
      return of(payload);
    }

    return this.http
      .put<DatasetFormulaPayload>(
        buildSelfServiceApiUrl(`/api/dvm/self-service/datasets/${encodeURIComponent(datasetId)}/formulas`),
        payload,
      )
      .pipe(
        map((response) => this.normalizeFormulaPayload(response)),
        catchError(() => {
          this.writeLocalFormulas(fileName, payload);
          return of(payload);
        }),
      );
  }

  syncToDeam(payload: Record<string, unknown>): Observable<unknown> {
    return this.http.post(
      buildSelfServiceApiUrl('/api/deam/upload'),
      payload,
    );
  }

  private normalizeFormulaPayload(response: DatasetFormulaPayload | null | undefined): DatasetFormulaPayload {
    return {
      computedColumns: Array.isArray(response?.computedColumns) ? response!.computedColumns : [],
      rowFilter: typeof response?.rowFilter === 'string' ? response.rowFilter : '',
      customAggregations: Array.isArray(response?.customAggregations) ? response!.customAggregations : [],
    };
  }

  private readLocalFormulas(fileName: string): DatasetFormulaPayload {
    try {
      const raw = window.localStorage.getItem(SelfServiceDatasetService.DATASET_FORMULAS_STORAGE_KEY);
      const all = raw ? JSON.parse(raw) : {};
      const entry = all?.[fileName];
      return this.normalizeFormulaPayload(entry);
    } catch {
      return this.normalizeFormulaPayload(undefined);
    }
  }

  private writeLocalFormulas(fileName: string, payload: DatasetFormulaPayload): void {
    if (!fileName) {
      return;
    }

    try {
      const raw = window.localStorage.getItem(SelfServiceDatasetService.DATASET_FORMULAS_STORAGE_KEY);
      const all = raw ? JSON.parse(raw) : {};
      all[fileName] = payload;
      window.localStorage.setItem(
        SelfServiceDatasetService.DATASET_FORMULAS_STORAGE_KEY,
        JSON.stringify(all),
      );
    } catch {
      // Best-effort fallback only.
    }
  }
}
